// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import moment from 'moment';
import { UilQuestionCircle, UilInfoCircle, UilImport } from '@iconscout/react-unicons';
import _ from 'lodash';
// Internal module imports
import AfrrClasses from './index.module.css';
import { DropdownComponent, ToggleButtonWithState } from '../../../../components/Inputs/Input';
import classes from '../../../../styles/AllDevices.module.css';
import CustomTimePicker from '../../../../components/Inputs/TimePicker/TimePicker';
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import { RootAfrrService } from '../../../../services/RootAfrrService';
import { isValidTimeRange, momentTimeFormater } from '../../../../utils/timeHelper';
import EChart from '../../../../components/Echarts/Echarts';
import { timeOnlyWithms } from '../../../../utils/dateHelper';
import { usToEuCurrencyFormat } from '../../../../utils/currencyHelper';
import { truncateNumber } from '../../../../utils/numberHelper';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import { DateParam, StringParam } from 'use-query-params';
import Table from '../../../../components/Table/Table';
import Typography from '../../../../components/Typography/Typography';
import IconComponent from '../../../../components/Icon/Icon';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import MFRRActivationSignalViewer from '../MFRR/ModalComponent/MFRRActivationSignalViewer';
import { exportData } from '../../../../utils/downloadHelper';

const EicSignalLogs = ({ eicCodeOptions, area, cId }) => {
    const curCetDateTime = moment().tz('Europe/Berlin');
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('date', DateParam, new Date(curCetDateTime.format()), cId, { clearOnUnmount: false });
    const [startTime, setStartTime] = useQueryParamWithDefault('startTime', StringParam, momentTimeFormater().subtract(15, 'minute').format('HH:mm:ss'), cId, {
        clearOnUnmount: false,
    });
    const [endTime, setEndTime] = useQueryParamWithDefault('endTime', StringParam, momentTimeFormater().format('HH:mm:ss'), cId, { clearOnUnmount: false });
    const [selectedEicCode, setSelectedEicCode] = useQueryParamWithDefault('eic', StringParam, eicCodeOptions[0]?.value, cId, { clearOnUnmount: false });
    const [chartData, setChartData] = useState([]);
    const [lastReported, setLastReported] = useState('');
    const [isAutoRefresh, setIsAutorefresh] = useState(false);
    const [seconds, setSeconds] = useState(30);
    const [counterInterval, setCounterInterval] = useState(null);
    const [activationEvents, setActivationEvents] = useState([]);
    const [selectedChip, setSelectedChip] = useState(null);
    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });

    const [activationEdgeEvents, setActivationEdgeEvents] = useState({
        tableData: [],
        count: 0,
    });
    const [signalViewerModal, setSignalViewerModal] = useState({ status: false, data: null });

    useEffect(() => {
        if (eicCodeOptions?.length) setSelectedEicCode(selectedEicCode ? selectedEicCode : eicCodeOptions[0]?.value);
    }, [eicCodeOptions]);

    useEffect(() => {
        if (selectedEicCode) {
            getActivationEvents();
        }
    }, [selectedEicCode]);

    useEffect(() => {
        if (selectedEicCode && startTime) {
            getEicSignalLogs();
        }
    }, [selectedEicCode, startTime]);

    useEffect(() => {
        if (selectedEicCode && startTime) getActivationEdgeEvents();
    }, [selectedEicCode, startTime, paginationQuery.skip]);

    const getActivationEvents = (isLoader = true) => {
        const params = {
            eicCode: selectedEicCode,
            area,
            date: moment(date).format('YYYY-MM-DD'),
        };
        RootAfrrService.GetActivationEvents(
            params,
            isLoader ? () => startLoader('getActivationEvents') : () => {},
            handleActivationEventsSuccess,
            handleError,
            isLoader ? () => stopLoader('getActivationEvents') : () => {}
        );
    };

    const handleActivationEventsSuccess = ({ data }) => {
        if (Array.isArray(data?.data)) {
            setActivationEvents(data.data);
        }
    };

    const getEicSignalLogs = (isLoader = true, currentEndTime = null, currentStartTime = null) => {
        if (currentEndTime) setEndTime(currentEndTime);
        if (currentStartTime) setStartTime(currentStartTime);

        const effectiveStartTime = currentStartTime ? currentStartTime : startTime;
        const effectiveEndTime = currentEndTime ? currentEndTime : endTime;

        if (!isValidTimeRange(effectiveStartTime, effectiveEndTime)) {
            toast.error('Invalid time range!');
            return;
        }

        const params = {
            eicCode: selectedEicCode,
            date: moment(date).format('YYYY-MM-DD'),
            startTime: effectiveStartTime,
            endTime: effectiveEndTime,
            area: area,
        };

        RootAfrrService.GetEicSignalLogs(
            params,
            isLoader ? () => startLoader('signalLogs') : () => {},
            handleLogsSuccess,
            handleError,
            isLoader ? () => stopLoader('signalLogs') : () => {}
        );
    };

    const handleLogsSuccess = ({ data }) => {
        setChartData(data?.data?.data || []);
        setLastReported(data?.data?.lastReported ? momentTimeFormater(data?.data?.lastReported?.timestamp).format('YYYY-MM-DD HH:mm:ss') : '');
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const formatYAxisValue = (value) => {
        return usToEuCurrencyFormat(value, 3);
    };

    const setActivationTime = (startTime, endTime) => {
        const start = momentTimeFormater(startTime).subtract(3, 'seconds').format('HH:mm:ss');
        const end = momentTimeFormater(endTime).add(1, 'minutes').format('HH:mm:ss');
        setStartTime(start);
        setEndTime(end);
        getEicSignalLogs(true, end, start);
    };

    useEffect(() => {
        if (isAutoRefresh) {
            const interval = setInterval(() => {
                if (seconds > 0) {
                    setSeconds(seconds - 1);
                } else {
                    getEicSignalLogs(false, momentTimeFormater().format('HH:mm:ss'), momentTimeFormater().subtract(30, 'minutes').format('HH:mm:ss'));
                    getActivationEvents(false);
                    setSeconds(30);
                }
            }, 1000);
            setCounterInterval(interval);
            return () => clearInterval(interval);
        }
    }, [isAutoRefresh, seconds]);

    const getActivationEdgeEvents = (paginatedData = true) => {
        const params = {
            eicCode: selectedEicCode,
            date: moment(date).format('YYYY-MM-DD'),
            startTime: startTime,
            endTime: endTime,
            area: area,
            ...(paginatedData
                ? {
                      skip: paginationQuery.skip,
                      limit: paginationQuery.limit,
                  }
                : {}),
        };
        const handler = paginatedData ? handleEdgeEventsSuccess : handleDownload;

        RootAfrrService.GetActivationEdgeEvents(params, startLoader, handler, handleError, stopLoader);
    };

    const handleEdgeEventsSuccess = ({ data }) => {
        const proccesedData = data?.data?.finalData.map((item) => ({
            customer: item.customerId.name,
            instance: item.groupId.name,
            edge: item.edgeAlias,
            bidCapacity: item.maxCapacity || '--',
            askedAndMaxPower: `${_.get(item, 'maxAskedPower', '--')} / ${_.get(item, 'maxDeliveredPower', '--')}`,
            status: item.status,
            startTime: momentTimeFormater(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(item.endTime).format('YYYY-MM-DD HH:mm:ss'),
            action: (
                <div onClick={() => setSignalViewerModal({ status: true, data: { ...item, signalId: _.get(item, ['properties', 'signalId']) } })}>
                    <IconComponent icon={UilInfoCircle} tooltip={'Signal Viewer'} />
                </div>
            ),
        }));

        setActivationEdgeEvents({
            tableData: proccesedData,
            count: data?.data?.count,
        });
    };

    const handleSubmit = () => {
        getEicSignalLogs();
        getActivationEvents();
        getActivationEdgeEvents();
    };

    const selectedChidStyle = (chipId, status) => {
        let boxShadowColor;
        let style = {};

        if (status === 'Success') boxShadowColor = 'green';
        else if (status === 'OverDelivered' || status === 'UnderDelivered') boxShadowColor = 'red';
        else if (status === 'Ongoing') boxShadowColor = '#175094';
        else if (status === 'UnReal') boxShadowColor = '#4a4a4a';

        if (selectedChip === chipId)
            style = {
                boxShadow: `0 0 2px 2px ${boxShadowColor}`,
                transform: 'scale(1.05)',
            };

        return style;
    };

    const handlePageChange = (pageno) => {
        setPaginationQuery({
            ...paginationQuery,
            skip: paginationQuery.limit * pageno,
            page: pageno,
        });
    };

    const handleDownload = ({ data }) => {
        const proccesedData = data?.data?.finalData.map((item) => ({
            customer: item.customerId.name,
            instance: item.groupId.name,
            edge: item.edgeAlias,
            bidCapacity: item.maxCapacity || '--',
            askedAndMaxPower: `${_.get(item, 'maxAskedPower', '--')} / ${_.get(item, 'maxDeliveredPower', '--')}`,
            status: item.status,
            startTime: momentTimeFormater(item.startTime).format('YYYY-MM-DD HH:mm:ss'),
            endTime: momentTimeFormater(item.endTime).format('YYYY-MM-DD HH:mm:ss'),
        }));
        exportData(proccesedData, 'aFRR Edge Activation Events');
    };

    return (
        <div>
            <div>
                <div className={AfrrClasses.Filter}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div className={AfrrClasses.FilterContainer}>
                            <div>
                                <div className={classes.FieldControl}>
                                    <label>Select EIC Code</label>
                                    <DropdownComponent
                                        name="eicCode"
                                        options={eicCodeOptions}
                                        defaultValue={{ value: selectedEicCode }}
                                        onChange={(e) => {
                                            if (e.value !== selectedEicCode) {
                                                setSelectedEicCode(e.value);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={AfrrClasses.FilterContainer}>
                            <div className={AfrrClasses.LeftContainer}>
                                {date && (
                                    <div className={AfrrClasses.SelecterContainer}>
                                        <label className={AfrrClasses.DatePickerLabel}>Date</label>
                                        <DatePicker date={date} setDate={setDate} showArrow={true} />
                                    </div>
                                )}

                                {startTime && endTime && (
                                    <>
                                        <div className={AfrrClasses.SelecterContainer}>
                                            <label className={AfrrClasses.DatePickerLabel}>Start Time</label>
                                            <CustomTimePicker
                                                startTime={startTime}
                                                setStartTime={(e) => {
                                                    setStartTime(e);
                                                }}
                                                type="start"
                                                maxDetail={'second'}
                                            />
                                        </div>
                                        <div className={AfrrClasses.SelecterContainer}>
                                            <label className={AfrrClasses.DatePickerLabel}>End Time</label>
                                            <CustomTimePicker
                                                endTime={endTime}
                                                setEndTime={(e) => {
                                                    setEndTime(e);
                                                }}
                                                type="end"
                                                maxDetail={'second'}
                                            />
                                        </div>
                                    </>
                                )}

                                <div style={{ display: 'flex', gap: '0.5vw', alignItems: 'baseline' }}>
                                    <div onClick={handleSubmit}>
                                        <button type="submit" className="btn-primary">
                                            Submit
                                        </button>
                                    </div>
                                    <div className={AfrrClasses.TimerContainer}>
                                        <ToggleButtonWithState
                                            label={'Auto Refresh'}
                                            value={isAutoRefresh}
                                            style={{ marginTop: '0' }}
                                            onChange={(e) => {
                                                setIsAutorefresh(e.target.checked);
                                                clearInterval(counterInterval);
                                                setSeconds(30);
                                            }}
                                        />
                                        {isAutoRefresh && <label className={AfrrClasses.TimerLabel}>in {seconds} seconds</label>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={AfrrClasses.Filter}>
                    <div className={AfrrClasses.ChipsHeader}>
                        <div className={AfrrClasses.FilterContainer}>
                            <div className={classes.FieldControl}>
                                <label className={AfrrClasses.ActLabel}>
                                    Activation Events
                                    <CustomTooltip
                                        background="#f4f7fc"
                                        content={
                                            <div>
                                                <div className={AfrrClasses.ActLabelD}>
                                                    <div className={`${AfrrClasses.ColorBlock} ${AfrrClasses.EventStatusOngoing}`}></div> <label>Ongoing</label>
                                                </div>
                                                <div className={AfrrClasses.ActLabelD}>
                                                    <div className={`${AfrrClasses.ColorBlock} ${AfrrClasses.EventStatusSuccess}`}></div> <label>Success</label>
                                                </div>
                                                <div className={AfrrClasses.ActLabelD}>
                                                    <div className={`${AfrrClasses.ColorBlock} ${AfrrClasses.EventStatusOverDelivered}`}></div>{' '}
                                                    <label>Under Delivered / Over Delivered</label>
                                                </div>
                                                <div className={AfrrClasses.ActLabelD}>
                                                    <div className={`${AfrrClasses.ColorBlock} ${AfrrClasses.EventStatusUnReal}`}></div>{' '}
                                                    <label>Un Real - Activation Lasted {'<'} 30S</label>
                                                </div>
                                            </div>
                                        }
                                    >
                                        <UilQuestionCircle size="1vw" />
                                    </CustomTooltip>
                                </label>
                            </div>
                        </div>
                        <label className={AfrrClasses.Link} onClick={getActivationEvents}>
                            Click To Refresh
                        </label>
                    </div>
                    {activationEvents.length > 0 ? (
                        <div className={AfrrClasses.ChipsContainer}>
                            {activationEvents.map((event) => (
                                <div
                                    className={`${AfrrClasses.Chips} ${AfrrClasses[`EventStatus${event.status}`]}`}
                                    onClick={() => {
                                        setActivationTime(event.startTime, event.endTime ? event.endTime : momentTimeFormater());
                                        setSelectedChip(event._id);
                                    }}
                                    style={selectedChidStyle(event?._id, event?.status)}
                                >
                                    <div>
                                        {momentTimeFormater(event.startTime).format('HH:mm:ss')} - {event.endTime ? momentTimeFormater(event.endTime).format('HH:mm:ss') : '-?'}
                                    </div>
                                    <div>
                                        R: {truncateNumber(Number(event.maxAskedPower), 3)}, D: {truncateNumber(Number(event.maxDeliveredPower), 3)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className={AfrrClasses.FilterContainer}>
                            <div className={classes.FieldControl}>
                                <label>No Events Found!</label>
                            </div>
                        </div>
                    )}
                </div>

                <div className={AfrrClasses.EicChart}>
                    <EChart
                        series={chartData || []}
                        title={'Picasso Signals'}
                        lastReported={lastReported}
                        xFormatter={(v) => timeOnlyWithms(momentTimeFormater(v))}
                        yFormatter={formatYAxisValue}
                        type="line"
                        yTooltipSuffix={['MWh', 'MWh', 'MWh']}
                    />
                </div>

                <div className={classes.AllDevices}>
                    <div className={classes.Header}>
                        <div>
                            <Typography content="Edge Activations" />
                            <div className={classes.TableCount}>
                                Total Count :
                                <span>
                                    <Typography size="14" content={activationEdgeEvents.count} />
                                </span>
                            </div>
                        </div>
                        <div>
                            <div onClick={() => getActivationEdgeEvents(false)}>
                                <IconComponent icon={UilImport} tooltip={'Download'} />
                            </div>
                        </div>
                    </div>
                    <div>
                        <div>
                            <Table
                                head={['Customer', 'Instance', 'Edge', 'Bid capacity', 'Ask. Power / Max. Power', 'Status', 'Start Time (CET)', 'End Time (CET)', 'Action']}
                                keys={['customer', 'instance', 'edge', 'bidCapacity', 'askedAndMaxPower', 'status', 'startTime', 'endTime', 'action']}
                                data={activationEdgeEvents.tableData}
                                page={paginationQuery.page}
                                Pagination={true}
                                limit={paginationQuery.limit}
                                handlePageChange={handlePageChange}
                                totalCount={activationEdgeEvents.count}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ModalComponent
                isOpen={signalViewerModal.status}
                setOpen={() => {
                    setSignalViewerModal({ status: false, data: null });
                }}
            >
                <MFRRActivationSignalViewer setOpen={setSignalViewerModal} data={signalViewerModal.data} />
            </ModalComponent>
        </div>
    );
};

export default EicSignalLogs;
